import React, { useState, useMemo } from 'react';
import { withRouter } from 'react-router';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import FlightLandIcon from '@material-ui/icons/FlightLand';
import styled, { ThemeProvider } from 'styled-components';
import AirportTransport from 'components/SearchBarComponents/AirportTransport/AirportTransport';
import Oneway from 'components/SearchBarComponents/Oneway/Oneway';
import {
  onErrorHandler,
  ErrorBoundaryFallbackComponent,
  withErrorBoundary
} from 'components/ErrorBoundry/ErrorBoundry';
import { sendGtagEvent } from 'utils/gtag';
import theme, { media } from 'theme/styled-theme';
import config from '../../config';

const Root = styled.div`
  z-index: 1;
  background-color: #ffffff;

  ${media.forPhoneOnly`
    min-width: calc(100vw - 32px);
  `}
`;

const Tabs = styled.ul`
  display: flex;
  list-style: none;
  align-items: center;
  margin: 0;
  padding: 0;
  min-height: 53px;
  background-color: ${props => props.theme.greyLight60};
  border-radius: 4px 4px 0 0;
  box-shadow: inset 2px -1px 6px 0 rgba(0,0,0,0.08);
  overflow: hidden;

  ${media.forPhoneOnly`
    min-height: 48px;
  `}
`;

const TabButton = styled.a`
  outline: none;
  margin-top: 1px;
  display: flex;
  flex: 1;
  justify-content: center;
`;

const TabItem = styled.li`
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 1rem;
  padding: 1rem 1.375rem;
  color: ${props => props.theme.greyLight200};
  width: 100%;
  justify-content: center;
  background: ${props => props.active ? '#ffffff' : 'inherit'};
  color: ${props => props.active
    ? props.theme.greyLight300
    : props.theme.greyLight200};
  font-weight: ${props => props.active ? 700 : 400};

  transition: background-color 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  ${media.forPhoneOnly`
    font-size: 0.75rem;
    padding: 0.875rem 0.75rem;
  `}
`;

const ContentWrap = styled.div`
  color: ${props => props.theme.greyLight200}};
`;

const Content = styled.div`
  display: ${props => props.active ? 'block' : 'none'};
`;

const TabIcon = styled.span`
  display: flex;
  align-items: center;
  margin-right: 0.375rem;

  svg, img {
    width: 20px!important;
    height: 20px!important;
    fill: ${props => props.theme.greyLight200}!important;
  }
`;

const TABS = {
  TOURS: 'TOURS',
  AIRPORT: 'AIRPORT',
  ONEWAY: 'ONEWAY',
};

const LandingSearchFeatures = props => {
  const { t } = props;
  const cdnPrefix = `${config.cdnUrl}/misc`;
  const iconOneWaySrc = `${cdnPrefix}/icons/icon-one-way.svg`;

  const [activeTab, setActiveTab] = useState(TABS.AIRPORT);

  const onTabClick = (e, _activeTab) => {
    e.stopPropagation();
    sendGtagEvent('Change Tab', 'Landing Search Bar', _activeTab);
    setActiveTab(_activeTab);
  };

  return (
    <ThemeProvider theme={theme}>
      <Root>
        <Tabs>
          <TabButton
            data-cy="cy-searchbar-tab-airport"
            role="button"
            tabIndex={0}
            onClick={e => { onTabClick(e, TABS.AIRPORT); }}
            onKeyDown={e => { onTabClick(e, TABS.AIRPORT); }}
          >
            <TabItem active={activeTab === TABS.AIRPORT}>
              <TabIcon><FlightLandIcon /></TabIcon>
              <span>{t('SEARCHBAR.TAB_AIRPORT')}</span>
            </TabItem>
          </TabButton>
          <TabButton
            data-cy="cy-searchbar-tab-oneway"
            role="button"
            tabIndex={0}
            onClick={e => { onTabClick(e, TABS.ONEWAY); }}
            onKeyDown={e => { onTabClick(e, TABS.ONEWAY); }}
          >
            <TabItem active={activeTab === TABS.ONEWAY}>
              <TabIcon>
                <img src={iconOneWaySrc} alt={t('SEARCHBAR.TAB.ONEWAY')} />
              </TabIcon>
              <span>{t('SEARCHBAR.TAB_ONEWAY')}</span>
            </TabItem>
          </TabButton>
        </Tabs>
        <ContentWrap>
          <Content active={activeTab === TABS.AIRPORT}>
            <AirportTransport />
          </Content>
          <Content active={activeTab === TABS.ONEWAY}>
            <Oneway />
          </Content>
        </ContentWrap>
      </Root>
    </ThemeProvider>
  );
};

LandingSearchFeatures.propTypes = {
  t: PropTypes.func,
};

LandingSearchFeatures.defaultProps = {
  t: () => {},
};

export default compose(
  withTranslation(['common']),
  withRouter,
)(withErrorBoundary(
  LandingSearchFeatures,
  ErrorBoundaryFallbackComponent,
  onErrorHandler,
));
